// Three.js scene rendering directly from a GameSnapshot.
//
// Reads `snapshot.scene.children`, partitions into tiles + cities by `type`,
// hands off to dedicated renderers. Camera is overhead-orbital with damping;
// the player owns it, except for one-shot go-to flights (camera/) and
// milestone cinematics (../cinematics/).

import type { SceneNode } from '@aod/protocol';
import { OrbitControls } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import { ActionMode } from '../action/ActionMode.js';
import { CinematicCameraRig, type OrbitControlsLike } from '../cinematics/CinematicCameraRig.js';
import { useSettings } from '../settings/useSettings.js';
import { tickToGameTime } from '../sim/gameTime.js';
import { Birds, DistantClouds } from './Ambient.js';
import { AmbientParticles } from './AmbientParticles.js';
import { AtmosphericClouds } from './AtmosphericClouds.js';
import { BoardScaleRefit } from './BoardScaleRefit.js';
import { BridgeTargetRings, isEngineer } from './BridgeTargetRings.js';
import { CoastalDressing } from './CoastalDressing.js';
import { ShadowSpots, buildCityShadowSpots, buildUnitShadowSpots } from './ContactShadows.js';
import { DayNightLighting } from './DayNightLighting.js';
import { OpenSea, SkyDome } from './Environment.js';
import { GroundFog } from './GroundFog.js';
import { HeroPennants } from './HeroPennants.js';
import { HexGrid } from './HexGrid.js';
import { Infrastructure } from './Infrastructure.js';
import { MountainSnow } from './MountainSnow.js';
import { MoveTargetRings } from './MoveTargetRings.js';
import { NightLights } from './NightLights.js';
import { PostProcessing } from './PostProcessing.js';
import { RainShower } from './RainShower.js';
import { RenderStatsCollector } from './RenderStatsCollector.js';
import { SelectionRing } from './SelectionRing.js';
import { Settlements } from './Settlements.js';
import { TerritoryOverlay } from './TerritoryOverlay.js';
import { TileDressing } from './TileDressing.js';
import { TileImprovements } from './TileImprovements.js';
import { VfxLayer } from './VfxLayer.js';
import { VoxelCities } from './VoxelCities.js';
import { VoxelUnits } from './VoxelUnits.js';
import { DOUBLE_SCALE, scalePoint, setBoardScale } from './boardScale.js';
import { CameraFlight } from './camera/CameraFlight.js';
import { CameraPanClamp } from './camera/CameraPanClamp.js';
import { CameraPanInput } from './camera/CameraPanInput.js';
import { CameraViewPublisher } from './camera/CameraViewPublisher.js';
import type { FlightControls } from './camera/flight.js';
import { findCapitalFocus } from './capitalFocus.js';
import { buildBiomeMap, tileElevationAt } from './elevation.js';
import { FaunaLayer } from './fauna/FaunaLayer.js';
import { isSinglePlayerSnapshot } from './isSinglePlayerSnapshot.js';
import { installSceneGlobals } from './scene/installSceneGlobals.js';
import type { SceneProps } from './scene/sceneProps.js';
import {
  useBoardCenter,
  useParticleBounds,
  useSceneNodes,
  useSeaFrame,
  useSnowPeaks,
} from './scene/useBoardGeometry.js';
import {
  CAMERA_FOV,
  FOG_COLOR,
  FOG_DENSITY,
  SKY_HORIZON,
  SKY_ZENITH,
  getCameraPosition,
} from './sceneRenderConfig.js';
import { SeaFrameLayer } from './sea/SeaFrameLayer.js';
import { climateAdjustedDay } from './seasons.js';

export function Scene({
  snapshot,
  events = [],
  localPlayerIndex = 0,
  onTileHover,
  onUnitHover,
  onTileClick,
  onCityClick,
  onUnitClick,
  onMoveClick,
  onBackgroundClick,
  selectedTileId,
  selectedCityId,
  selectedUnitId,
  focusRequest = null,
  doctrine = null,
  engagements,
  currentTick = 0,
  climate,
  sea,
  onBuildBridge,
}: SceneProps) {
  const { tiles: simTiles, cities, units } = useSceneNodes(snapshot);
  // The sea axis, the rendered tiles, the sea layer and the camera slack.
  const { tiles, landlocked, seaLayer, seaRings } = useSeaFrame(simTiles, sea);

  const resourceColors = useMemo<Record<string, string>>(() => {
    const globals = snapshot.state.globals as
      | { resources?: ReadonlyArray<{ id: string; color: string }> }
      | undefined;
    const map: Record<string, string> = {};
    for (const r of globals?.resources ?? []) {
      map[r.id] = r.color;
    }
    return map;
  }, [snapshot]);

  const biomeMap = useMemo(() => buildBiomeMap(tiles), [tiles]);
  // City hexes, as "q,r" keys. Keyed on the joined string so the Set (and the
  // tree scatter that depends on it) only changes when a city appears or goes.
  const settlementKey = cities
    .map(
      (c) =>
        `${(c.props as Record<string, unknown> | undefined)?.q},${(c.props as Record<string, unknown> | undefined)?.r}`,
    )
    .join('|');
  const settlementCoords = useMemo(
    () => new Set(settlementKey ? settlementKey.split('|') : []),
    [settlementKey],
  );

  // Fog of war: visible tile coords for the local player. Skipped entirely
  // in single-player so the map is fully visible from frame one.
  // (Audit 2026-07-16 visual finding.)
  const visibleCoords = useMemo(() => {
    if (isSinglePlayerSnapshot(snapshot)) return null;
    const globals = snapshot.state.globals as { visibility?: Record<string, string[]> } | undefined;
    const raw = globals?.visibility?.[String(localPlayerIndex)];
    if (!raw || raw.length === 0) return null; // null = no fog (show all)
    return new Set<string>(raw);
  }, [snapshot.state.globals, localPlayerIndex, snapshot]);

  // Accumulate all ever-visible coords for fog-of-war depth (explored vs unexplored).
  const exploredCoords = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (visibleCoords) {
      const explored = exploredCoords.current;
      for (const coord of visibleCoords) {
        explored.add(coord);
      }
    }
  }, [visibleCoords]);

  const selectedTileData = useMemo(() => {
    if (!selectedTileId) return null;
    const t = tiles.find((tile) => tile.id === selectedTileId);
    if (!t) return null;
    const props = t.props as Record<string, unknown> | undefined;
    const q = (props?.q as number) ?? 0;
    const r = (props?.r as number) ?? 0;
    return { position: t.transform.position, q, r };
  }, [tiles, selectedTileId]);

  const selectedUnitNode = useMemo(() => {
    if (!selectedUnitId) return null;
    return units.find((u) => u.id === selectedUnitId) ?? null;
  }, [units, selectedUnitId]);

  // Center camera on the centroid of the map for a good default framing.
  const center = useBoardCenter(tiles);

  // Bias the *initial* camera frame toward the local player's starting
  // capital. Derived once on first render via a ref so subsequent
  // re-renders don't drag the camera off whatever the user has panned
  // to via minimap/unit selection.
  const initialFocusRef = useRef<[number, number, number] | null>(null);
  if (initialFocusRef.current === null) {
    initialFocusRef.current = findCapitalFocus(snapshot, localPlayerIndex) ?? center;
  }
  const initialCameraCenter = initialFocusRef.current;
  // Milestone cinematics (CinematicCameraRig) and go-to flights
  // (CameraFlight) both drive the camera through these controls.
  const controlsRef = useRef<(OrbitControlsLike & FlightControls) | null>(null);

  // Particle spawn bounds from tile extents.
  const particleBounds = useParticleBounds(tiles);

  // Season tint input: the in-game day (1..365). Day-granular, so seasonal
  // colour work runs once per in-game day (~1s at 1× speed), not per tick.
  const seasonDay = useMemo(
    () => climateAdjustedDay(climate, tickToGameTime(currentTick).day),
    [climate, currentTick],
  );

  // Massif-core peaks: MountainSnow flurries fall over these.
  const snowPeaks = useSnowPeaks(tiles, biomeMap);

  // Large Board: world content renders at 2x inside a scaled <group>; the
  // camera, sky and post stack stay outside. Synced during render (not in an
  // effect) so mount-time closures — the Canvas camera prop and the
  // onCreated audit closures — read the correct live value.
  const doubleBoard = useSettings((st) => st.doubleBoard);
  const showTerritory = useSettings((st) => st.showTerritory);
  const showDayNight = useSettings((st) => st.showDayNight);
  const particleEffects = useSettings((st) => st.particleEffects);
  const showClouds = useSettings((st) => st.showClouds);
  const boardScale = doubleBoard ? DOUBLE_SCALE : 1;
  setBoardScale(boardScale);
  const scaledCenter = useMemo(() => scalePoint(center), [center, boardScale]);

  // Contact shadows: one blob per unit / city, rebuilt with each snapshot.
  const unitShadows = useMemo(() => buildUnitShadowSpots(units, biomeMap), [units, biomeMap]);
  const cityShadows = useMemo(() => buildCityShadowSpots(cities, biomeMap), [cities, biomeMap]);

  return (
    <Canvas
      data-testid="game-canvas"
      shadows
      camera={{ position: scalePoint(getCameraPosition(initialCameraCenter)), fov: CAMERA_FOV }}
      style={{
        position: 'absolute',
        inset: 0,
        // Fallback until the sky dome draws (matches its zenith → horizon).
        background: `linear-gradient(to bottom, ${SKY_ZENITH} 0%, ${SKY_HORIZON} 100%)`,
      }}
      onCreated={(state) => installSceneGlobals(state, controlsRef)}
      onPointerMissed={() => {
        onBackgroundClick?.();
      }}
    >
      <fogExp2 args={[FOG_COLOR, FOG_DENSITY]} attach="fog" />
      <RenderStatsCollector />
      <BoardScaleRefit scale={boardScale} initialTarget={scaledCenter} controls={controlsRef} />
      <SkyDome />
      <OpenSea color={landlocked ? FOG_COLOR : undefined} />
      <DistantClouds center={scaledCenter} />
      <Birds center={scaledCenter} />
      <DayNightLighting enabled={showDayNight} />
      <group scale={boardScale}>
        <HexGrid
          tiles={tiles}
          resourceColors={resourceColors}
          visibleCoords={visibleCoords}
          exploredCoords={exploredCoords.current}
          onPointerOver={onTileHover}
          onClick={onTileClick}
          settlementCoords={settlementCoords}
          seasonDay={seasonDay}
          climate={climate}
          detail={doubleBoard ? 2 : 1}
        />
        {seaLayer && <SeaFrameLayer tiles={tiles} mode={seaLayer.mode} frame={seaLayer.frame} />}
        <Infrastructure tiles={tiles} />
        <TileImprovements tiles={tiles} seasonDay={seasonDay} />
        <ShadowSpots spots={cityShadows} />
        <NightLights cities={cityShadows} units={unitShadows} />
        <MountainSnow peaks={snowPeaks} />
        {particleEffects && <RainShower tiles={tiles} />}
        <GroundFog
          tiles={tiles}
          biomeMap={biomeMap}
          visibleCoords={visibleCoords}
          currentTick={currentTick}
        />
        <VoxelCities
          cities={cities}
          onClick={onCityClick}
          selectedId={selectedCityId}
          biomeMap={biomeMap}
        />
        <Settlements cities={cities} biomeMap={biomeMap} onClick={onCityClick} />
        <VoxelUnits
          units={units}
          onClick={onUnitClick}
          onUnitHover={onUnitHover}
          selectedId={selectedUnitId}
          visibleCoords={visibleCoords}
          exploredCoords={exploredCoords.current}
          biomeMap={biomeMap}
          engagements={engagements}
          currentTick={currentTick}
        />
        <HeroPennants units={units} cities={cities} visibleCoords={visibleCoords} />
        <TileDressing tiles={tiles} visibleCoords={visibleCoords} />
        <CoastalDressing tiles={tiles} visibleCoords={visibleCoords} />
        <FaunaLayer tiles={tiles} biomeMap={biomeMap} visibleCoords={visibleCoords} />
        <ShadowSpots spots={unitShadows} />
        <VfxLayer events={events} />
        {selectedUnitNode && (
          <MoveTargetRings
            unit={selectedUnitNode}
            tiles={tiles}
            cities={cities}
            units={units}
            onMoveClick={onMoveClick}
          />
        )}
        {selectedUnitNode && isEngineer(selectedUnitNode) && (
          <BridgeTargetRings
            unit={selectedUnitNode}
            tiles={tiles}
            biomeMap={biomeMap}
            onBuild={onBuildBridge}
          />
        )}
        {selectedTileData && (
          <SelectionRing
            position={selectedTileData.position}
            surfaceY={tileElevationAt(selectedTileData.q, selectedTileData.r, biomeMap)}
          />
        )}
        {showTerritory && (
          <TerritoryOverlay tiles={tiles} visibleCoords={visibleCoords} biomeMap={biomeMap} />
        )}
        {particleBounds && (
          <AtmosphericClouds bounds={particleBounds} climate={climate} enabled={showClouds} />
        )}
        {particleBounds && <AmbientParticles bounds={particleBounds} count={200} day={seasonDay} />}
      </group>
      <PostProcessing hasSelection={!!selectedUnitId} doctrine={doctrine} />
      <CinematicCameraRig controls={controlsRef} />
      <CameraFlight controls={controlsRef} request={focusRequest} />
      <CameraViewPublisher controls={controlsRef} />
      <CameraPanInput controls={controlsRef} />
      <CameraPanClamp tiles={tiles} seaRings={seaRings} scale={boardScale} controls={controlsRef} />
      {new URLSearchParams(window.location.search).get('action') === '1' && (
        <ActionMode
          enabled
          controls={controlsRef}
          tiles={tiles}
          onExit={() => {
            const url = new URL(window.location.href);
            url.searchParams.delete('action');
            window.history.replaceState(null, '', url.toString());
          }}
        />
      )}
      <OrbitControls
        ref={controlsRef as never}
        enableDamping
        maxPolarAngle={Math.PI / 2.05}
        minDistance={6 * boardScale}
        maxDistance={60 * boardScale}
      />
    </Canvas>
  );
}
